/* eslint-disable no-param-reassign */
import {useEffect, useState} from 'react'
import {css} from '@emotion/react'
import debounce from 'lodash/debounce'
import {colors, gethomeLink} from '../../../constants'
import {isMac, isUnsupportedOS, isWindows} from '../../../util/userAgent'

import StartpageLogoWhite from '../../../images/browser/startpage-logo-white.svg'
import MacLaptop from '../../../images/browser/mac.svg'
import Button from './Button'
import {useOnce} from '../../../util/customHooks'

const CONTAINER_MAX_WIDTH = 966

const text = {
    en: {
        buttonText: `Download Startpage`,
        introText: `Introducing`,
        bodyText: `The desktop browsing app is here. Simple. Stealth. Secure.`,
    },
    de: {
        buttonText: `Startpage herunterladen`,
        introText: `Jetzt verfügbar:`,
        bodyText: `Die neue Desktop-Browser-App: Simple. Stealth. Secure.`,
    },
    nl: {
        buttonText: `Startpage downloaden`,
        introText: `Nu beschikbaar:`,
        bodyText: `De nieuwe desktop browser app is hier. Simple. Stealth. Secure.`,
    },
}

const Hero = ({experimentId, os, browser, languageCode}) => {
    const [title, setTitle] = useState(``)
    const [hasScrolled, setHasScrolled] = useState(false)

    const {buttonText, introText, bodyText} = text[languageCode]

    useEffect(() => {
        if (languageCode === 'de') {
            if (isUnsupportedOS()) {
                setTitle(`Startpage für Mac und Windows`)
                return
            }
            if (isMac()) setTitle(`Startpage für Mac`)
            if (isWindows()) setTitle(`Startpage für Windows`)
        } else if (languageCode === 'nl') {
            if (isUnsupportedOS()) {
                setTitle(`Startpage voor Mac en Windows`)
                return
            }
            if (isMac()) setTitle(`Startpage voor Mac`)
            if (isWindows()) setTitle(`Startpage voor Windows`)
        } else {
            if (isUnsupportedOS()) {
                setTitle(`Startpage for Mac and Windows`)
                return
            }
            if (isMac()) setTitle(`Startpage for Mac`)
            if (isWindows()) setTitle(`Startpage for Windows`)
        }
    }, [])

    useOnce(() => {
        const onScroll = debounce(() => {
            if (window.pageYOffset > 0) {
                setHasScrolled(true)
            } else {
                setHasScrolled(false)
            }
        }, 10)
        window.addEventListener('scroll', onScroll)
    })

    return (
        <section
            className="hero"
            css={css`
                width: 100%;
                background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQ0MCIgaGVpZ2h0PSI2OTgiIHZpZXdCb3g9IjAgMCAxNDQwIDY5OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjE0NDAiIGhlaWdodD0iNjE2IiBmaWxsPSJ1cmwoI3BhaW50MF9saW5lYXJfNjg4XzE0MzYyKSIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTAgMjg5QzcyLjY2NjcgMzE3IDI0NyAzNjkgNDA3IDM1M0M0NTQuNzMzIDM0OC4yMjcgNTE3LjkwMSAzMzYuMzkgNTkxLjM0MSAzMjIuNjNDODI1LjYxMiAyNzguNzMzIDExNjQuNCAyMTUuMjUzIDE0NDAgMjk5VjY3Ni45NTdDOTYxLjI4NCA2MTQuOTggNDc3Ljc5NyA2MjEuNiAwIDY5Ny4yNTFWMjg5WiIgZmlsbD0idXJsKCNwYWludDFfbGluZWFyXzY4OF8xNDM2MikiLz4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0icGFpbnQwX2xpbmVhcl82ODhfMTQzNjIiIHgxPSI3MjAiIHkxPSIwIiB4Mj0iNzIwIiB5Mj0iODM3LjYyIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CjxzdG9wIG9mZnNldD0iMC4yNzI5MTciIHN0b3AtY29sb3I9IiM2ODc5RkIiLz4KPHN0b3Agb2Zmc2V0PSIwLjgxMjUiIHN0b3AtY29sb3I9IiMyOUREQ0MiLz4KPC9saW5lYXJHcmFkaWVudD4KPGxpbmVhckdyYWRpZW50IGlkPSJwYWludDFfbGluZWFyXzY4OF8xNDM2MiIgeDE9IjEyMjYuNzQiIHkxPSI2Ny43NTk2IiB4Mj0iNzcuNzAxMyIgeTI9IjEwODMuMDYiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj4KPHN0b3Agc3RvcC1jb2xvcj0iIzY5N0FGQyIvPgo8c3RvcCBvZmZzZXQ9IjAuODQzMDEiIHN0b3AtY29sb3I9IiMyOUREQ0MiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8L3N2Zz4K);
                background-repeat: no-repeat;
                background-size: cover;
                padding: 35px 2rem 0;
                background-position: 50% 100%;
                height: 696px;
                margin-bottom: 250px;
            `}
        >
            <div
                className="header"
                css={css`
                    background: ${hasScrolled ? '#6779fb' : 'transparent'};
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    padding: 25px 2rem;
                    box-shadow: ${hasScrolled ? '0px 20px 26px rgba(0, 0, 0, 0.1)' : 'none'};
                    z-index: 9998;
                `}
            >
                <div
                    className="header-inner"
                    css={css`
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        max-width: ${CONTAINER_MAX_WIDTH}px;
                        margin: 0 auto;
                    `}
                >
                    <a
                        href={gethomeLink(languageCode)}
                        target="__blank"
                        rel="noopener noreferrer nofollow"
                        className="logo"
                        css={css`
                            display: block;
                        `}
                    >
                        <StartpageLogoWhite />
                    </a>
                    <Button
                        text={buttonText}
                        styleVariant="header"
                        ctaType="cta-1"
                        experiment={experimentId}
                        os={os}
                        browser={browser}
                        languageCode={languageCode}
                    />
                </div>
            </div>

            <div
                css={css`
                    max-width: ${CONTAINER_MAX_WIDTH}px;
                    margin: 70px auto;
                `}
            >
                <div
                    className="billboard"
                    css={css`
                        margin: 45px 0 0;
                        text-align: center;
                        h1,
                        p {
                            color: ${colors.whisperLight};
                        }
                        h1 {
                            font-size: 56px;
                            margin: 0 0 12px;
                        }
                        .intro,
                        .title {
                            font-weight: 800;
                        }
                    `}
                >
                    <p className="intro">{introText}</p>
                    <h1
                        className="title"
                        css={css`
                            opacity: ${title === `` ? '0' : '1'};
                            transition: opacity 0.2s ease;
                            height: 70px;
                        `}
                    >
                        {title}
                    </h1>
                    <p>{bodyText}</p>
                    <div
                        className="cta-container"
                        css={css`
                            margin: 32px 0;
                        `}
                    >
                        <Button
                            text={buttonText}
                            ctaType="cta-2"
                            experiment={experimentId}
                            os={os}
                            browser={browser}
                            languageCode={languageCode}
                        />
                    </div>
                </div>
                <div
                    className="computer"
                    css={css`
                        display: flex;
                        justify-content: center;
                    `}
                >
                    <MacLaptop />
                </div>
            </div>
        </section>
    )
}

export default Hero
